export type CustomMapButtonPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface CustomMapButton {
    text: string;
    icon: string;
    position?: CustomMapButtonPosition;
    click: () => void;
    subButtons?: CustomMapButton[];
}

export class MapCustomButtons {
    private readonly items = new Set<CustomMapButton>();
    private readonly mapElement: HTMLElement;

    constructor(mapElement: HTMLElement) {
        this.mapElement = mapElement;
    }

    public add(button: CustomMapButton): CustomMapButton {
        this.items.add(button);
        this.renderButton(button);
        return button;
    }

    private renderButton(button: CustomMapButton): void {
        const buttonElement = this.createButtonElement(button);
        this.getOrCreateContainer(button.position).appendChild(buttonElement);
    }

    private createButtonElement(button: CustomMapButton): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';
        
        const btn = this.createBaseButton(button);
        wrapper.appendChild(btn);

        if (button.text) {
            const tooltip = this.createTooltipElement(button.text, button.position);
            wrapper.appendChild(tooltip);
            btn.addEventListener('mouseover', () => tooltip.style.display = 'block');
            btn.addEventListener('mouseout', () => tooltip.style.display = 'none');
            btn.addEventListener('click', () => tooltip.style.display = 'none');
        }

        if (button.subButtons?.length) {
            const subContainer = this.createSubButtonContainer(button);
            wrapper.appendChild(subContainer);
            btn.addEventListener('click', (event) => {
                event.stopPropagation();
                subContainer.style.display = subContainer.style.display === 'none' ? 'flex' : 'none';
            });
        }

        document.addEventListener('click', () => {
            wrapper.querySelectorAll('div[style*="display: flex"]').forEach(el => {
                (el as HTMLElement).style.display = 'none';
            });
        });

        return wrapper;
    }

    private createBaseButton(button: CustomMapButton): HTMLButtonElement {
        const btn = document.createElement('button');
        Object.assign(btn.style, {
            width: '40px', height: '40px', backgroundColor: 'white', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '15px'
        });
        
        const icon = document.createElement('img');
        Object.assign(icon.style, { width: '120%', height: '120%' });
        icon.src = button.icon;
        icon.alt = button.text;
        
        btn.appendChild(icon);
        btn.addEventListener('click', button.click);
        return btn;
    }

    private createTooltipElement(content: string, position?: CustomMapButtonPosition): HTMLElement {
        const tooltip = document.createElement('div');
        tooltip.className = 'custom-tooltip';
        tooltip.innerText = content;
        Object.assign(tooltip.style, {
            backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', position: 'absolute', padding: '5px 8px', top: '0px',
            borderRadius: '4px', whiteSpace: 'nowrap', fontSize: '12px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
            display: 'none', zIndex: '1000'
        });
        
        tooltip.style[position?.includes('right') ? 'right' : 'left'] = '100%';
        return tooltip;
    }

    private createSubButtonContainer(button: CustomMapButton): HTMLElement {
        const subContainer = document.createElement('div');
        Object.assign(subContainer.style, {
            display: 'none', flexDirection: 'column', position: 'absolute', top: '0px'
        });
        subContainer.style[button.position?.includes('right') ? 'right' : 'left'] = '100%';
        
        button.subButtons!.forEach(subButton => {
            subButton.position = button.position;
            const subBtnWrapper = document.createElement('div');
            subBtnWrapper.style.position = 'relative';
            
            const subBtnElement = this.createBaseButton(subButton);
            Object.assign(subBtnElement.style, { backgroundColor: 'white', margin: '5px' });
            subBtnWrapper.appendChild(subBtnElement);
            
            if (subButton.text) {
                const subTooltip = this.createTooltipElement(subButton.text, subButton.position);
                subBtnWrapper.appendChild(subTooltip);
                subBtnElement.addEventListener('mouseover', () => subTooltip.style.display = 'block');
                subBtnElement.addEventListener('mouseout', () => subTooltip.style.display = 'none');
            }
            
            subContainer.appendChild(subBtnWrapper);
        });
        
        return subContainer;
    }

    private getOrCreateContainer(position: CustomMapButtonPosition = 'top-right'): HTMLElement {
        const containerId = `map-button-container-${position}`;
        let container = this.mapElement.querySelector(`#${containerId}`) as HTMLElement | null;

        if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            Object.assign(container.style, { position: 'absolute', zIndex: '1000' });
            
            switch (position) {
                case 'top-right':
                    Object.assign(container.style, { top: '0em', right: '0em' });
                    break;
                case 'top-left':
                    Object.assign(container.style, { top: '0em', left: '0em' });
                    break;
                case 'bottom-right':
                    Object.assign(container.style, { bottom: '0em', right: '0em' });
                    break;
                case 'bottom-left':
                    Object.assign(container.style, { bottom: '0em', left: '0em' });
                    break;
            }
            
            this.mapElement.appendChild(container);
        }

        return container;
    }
}
